import { useEffect, useRef } from 'react'

import { AxiosError } from 'axios'
import { UserActivityAPI } from 'conversations/api'
import {
  getCurrentUser,
  getPreference,
  preferencesLoaded,
} from 'conversations/modules/user/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { setPreference } from 'shared/store/actions/user'
import { User } from 'shared/typings/user/User'
import useSWR from 'swr'

import { AvailabilityIF } from '@driftt/app-config/dist/utils/driftTypes'
import { useToast } from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { RootState } from 'typesafe-actions'

const PREF = 'autoAwayLastSeen'

enum UpdateReasonType {
  AUTHENTICATION = 'AUTHENTICATION',
  USER_MANUAL = 'USER_MANUAL',
  AUTO_AWAY = 'AUTO_AWAY',
}

interface AuditLog {
  userId: number
  orgId: number
  updaterId: number
  updateReasonType: UpdateReasonType
  previousValue: {
    availability: AvailabilityIF
  }
  updatedValue: {
    availability: AvailabilityIF
  }
  userUpdatedAt: number
  timestamp: number
}

const fetcher = (url: string) => UserActivityAPI.get(url).then((res) => res.data)

const useAutoAwayToast = () => {
  const triggerToast = useToast()

  const dispatch = useDispatch()

  const hasRepAvailability = access.hasGates('auto_rep_availability')

  const currentUser = useSelector<RootState, User | undefined>(getCurrentUser)
  const userIsAvailable = currentUser?.availability === AvailabilityIF.AVAILABLE

  const lastSeen = useSelector(getPreference(PREF))
  const prefLoaded = useSelector(preferencesLoaded)

  const { data: isEnabledForUser } = useSWR<boolean, AxiosError>(
    '/availability/auto/settings/user',
    fetcher,
    { revalidateOnFocus: false }
  )
  const doFetchLogs = hasRepAvailability && userIsAvailable && isEnabledForUser

  const { data } = useSWR<AuditLog[], AxiosError>(
    doFetchLogs ? `/availability/auditlog/user/${currentUser?.id}` : null,
    fetcher,
    { revalidateOnFocus: false }
  )
  const setOnce = useRef<boolean>(false)

  useEffect(() => {
    const log = data && data[0]
    const isSystemSet = log?.updateReasonType === UpdateReasonType.AUTO_AWAY
    const systemMarkedUserAvailable =
      isSystemSet &&
      log?.previousValue.availability === AvailabilityIF.OFFLINE &&
      log?.updatedValue.availability === AvailabilityIF.AVAILABLE

    const show = systemMarkedUserAvailable && userIsAvailable && lastSeen < log?.userUpdatedAt

    if (prefLoaded && data && !setOnce.current && show) {
      setOnce.current = true
      triggerToast({
        message:
          'You were recently set to "away" because you lost connection to Drift. No chats were routed to you while you were away.',
        variant: 'warning',
        options: {
          duration: 5000,
        },
      })
      dispatch(
        setPreference({
          preference: PREF,
          value: Date.now(),
        })
      )
    }
  }, [data, dispatch, lastSeen, prefLoaded, triggerToast, userIsAvailable])
}

export default useAutoAwayToast
